import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import {
  stripePromise,
  PRICING_INFO,
  BillingInterval,
} from '../config/stripeConfig';
import CheckoutForm from '../components/CheckoutForm';

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get plan type from URL parameter, default to monthly
  const planType = (searchParams.get('plan') as BillingInterval) || 'monthly';

  // Validate plan type
  const billingInterval: BillingInterval =
    planType === 'yearly' ? 'yearly' : 'monthly';

  // Get pricing info for selected plan
  const selectedPlan = PRICING_INFO[billingInterval];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Unveil</h1>
            <button
              onClick={() => navigate('/pricing')}
              className="text-gray-600 hover:text-gray-900"
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Side - Order Summary */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Order Summary
            </h2>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {selectedPlan.displayPeriod} Subscription
                  </h3>
                  <p className="text-sm text-gray-600">
                    Billed{' '}
                    {billingInterval === 'yearly' ? 'annually' : 'monthly'}
                  </p>
                  {billingInterval === 'yearly' && (
                    <p className="text-xs text-green-600 font-medium mt-1">
                      {PRICING_INFO.yearly.savings} •{' '}
                      {PRICING_INFO.yearly.savingsPercentage} off
                    </p>
                  )}
                </div>
                <p className="text-xl font-bold text-gray-900">
                  {selectedPlan.displayPrice}
                </p>
              </div>

              {/* Show monthly equivalent for yearly */}
              {billingInterval === 'yearly' && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    <strong>{PRICING_INFO.yearly.monthlyEquivalent}</strong>
                  </p>
                </div>
              )}

              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-900">
                    {selectedPlan.displayPrice}
                  </span>
                </div>
                <div className="flex justify-between items-center font-bold text-lg pt-2 border-t">
                  <span>Total due</span>
                  <span>{selectedPlan.displayPrice}</span>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-900">
                  <strong>Recurring billing:</strong> You'll be charged{' '}
                  {selectedPlan.displayPrice} every {selectedPlan.interval}{' '}
                  until you cancel.
                </p>
              </div>
            </div>
          </div>

          {/* Right Side - Payment Form */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Payment Details
            </h2>
            <div className="bg-white rounded-lg shadow-md p-6">
              {stripePromise ? (
                <Elements stripe={stripePromise}>
                  <CheckoutForm priceId={selectedPlan.priceId} />
                </Elements>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">Loading payment form...</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-12 text-center">
          <div className="flex justify-center items-center space-x-8 text-gray-500">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
              Secure 256-bit SSL encryption
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Powered by Stripe</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
